// Persisted session identity: the (accountId, deviceId, displayName)
// triple that `bootstrapWebSession` needs to re-hydrate the Zustand
// session store. Stored in the same IndexedDB `settings_kv` table as
// the wrapped refresh token but kept in a separate module so the
// secret-vs-non-secret boundary stays obvious.
//
// Display name is NOT a secret per the Wave-3 contract (it ships on
// the wire on every message), but accountId/deviceId/displayName
// together are identity-adjacent — see Hard rule 3. They are not
// logged and not echoed back in error messages. The whole triple is
// wiped by `clearIdentity()` on logout / refresh-failure.

import { db } from './db';

const IDENTITY_KEY = '__session.identity';

export interface PersistedIdentity {
  readonly accountId: string;
  readonly deviceId: string;
  readonly displayName: string;
}

export async function storeIdentity(identity: PersistedIdentity): Promise<void> {
  await db.settings_kv.put({
    key: IDENTITY_KEY,
    value: { ...identity },
    updatedAt: Date.now(),
  });
}

export async function retrieveIdentity(): Promise<PersistedIdentity | null> {
  const record = await db.settings_kv.get(IDENTITY_KEY);
  if (!record?.value) {
    return null;
  }
  const v = record.value as Partial<PersistedIdentity>;
  if (
    typeof v.accountId !== 'string' ||
    typeof v.deviceId !== 'string' ||
    typeof v.displayName !== 'string'
  ) {
    return null;
  }
  return { accountId: v.accountId, deviceId: v.deviceId, displayName: v.displayName };
}

export async function clearIdentity(): Promise<void> {
  await db.settings_kv.delete(IDENTITY_KEY);
}
